import gsap from 'gsap';
import { Draggable } from 'gsap/Draggable';
import { DraggableComponent } from '../../draggable/draggable.component';
import { DropZoneComponent } from '../../drop-zone/drop-zone.component';
import { DraggableComponentEvent } from '../events/drag-drop-component.event';
import { DragCallback } from '../types/dragg-callback.type';

interface Element extends HTMLElement {
  getBoundingClientRect(): DOMRect;
  setColliding(colliding: boolean): void;
}

export class DragDropService {
  #dropzones: Array<DropZoneComponent> = [];

  #draggableContainers: Array<Element> = [];

  #standaloneDraggables: Array<DraggableComponent> = [];

  #dropper?: HTMLElement;

  #useDropper = false;

  #disableGsapAutoscroll: boolean;

  #startPositionDropper = { x: 0, y: 0 };

  #startPositionDrag = { x: 0, y: 0 };

  #startPositionInComponent = { x: 0, y: 0 };

  #initialScrollPosition = 0;

  #resetDropperOutside = true;

  #parentNode?: ParentNode;

  get parentNode(): ParentNode | undefined {
    return this.#parentNode;
  }

  get dropzones(): Array<DropZoneComponent> {
    return this.#dropzones;
  }

  static ANIMATION_TIME_IN_SECONDS = 0.2;

  static ANIMATION_SCALE = 0.925;

  static SCROLL_MARGIN = 80;

  static DELAY_BROADCAST_VALUE = 20;

  static DELAY_AFTER_ANIMATION = 10;

  enableDropper() {
    this.#useDropper = true;
  }

  disableDropper() {
    this.#useDropper = false;
  }

  disableResetDropperOutside() {
    this.#resetDropperOutside = false;
  }

  enableResetDropperOutside() {
    this.#resetDropperOutside = true;
  }

  registerDropZone(dropzone: DropZoneComponent) {
    if (!this.#dropzones.includes(dropzone)) {
      this.#dropzones.push(dropzone);
      dropzone.dropzoneService.setDragDropService(this);
      dropzone.dropzoneService.registerDraggablesChanged(
        this.#initializeDraggables.bind(this),
      );
    }
  }

  registerContainer(container: Element) {
    if (!this.#draggableContainers.includes(container)) {
      this.#draggableContainers.push(container);
    }
  }

  registerStandaloneDraggables(draggables: Array<DraggableComponent>) {
    this.#standaloneDraggables = [...this.#standaloneDraggables, ...draggables];
    this.#initializeDraggables(draggables, true);
  }

  unregisterStandaloneDraggables(draggables: Array<DraggableComponent>) {
    this.#standaloneDraggables = this.#standaloneDraggables.filter(
      (standaloneDraggable) => !draggables.includes(standaloneDraggable),
    );

    this.#destructDraggables(draggables);
  }

  unregisterDropZone(removedDropzone: DropZoneComponent) {
    this.#dropzones = this.#dropzones.filter(
      (dropzone) => dropzone !== removedDropzone,
    );
  }

  #initializeDraggables(
    draggables: Array<DraggableComponent>,
    standalone = false,
  ) {
    draggables.forEach((draggable) => {
      draggable.registerEvents({
        onPress: this.#onPress.bind(this),
        onDrag: this.#onDrag.bind(this),
        onRelease: this.#onRelease.bind(this),
      });
      draggable.initializeDraggable(standalone);
    });
  }

  #destructDraggables(draggables: Array<DraggableComponent>) {
    draggables.forEach((draggable) => {
      draggable.clearEvents();
    });
  }

  #onPress: DragCallback = (
    draggingComponent: DraggableComponent,
    event: PointerEvent,
  ) => {
    const dropZone = this.#getDropZoneByDraggable(draggingComponent);
    if (this.#useDropper) {
      this.#initializeDropper(draggingComponent, event);
    }

    const positionEvent =
      window.TouchEvent && event instanceof TouchEvent
        ? event.touches[0]
        : event;

    this.#startPositionDrag.x = positionEvent.clientX;
    this.#startPositionDrag.y = positionEvent.clientY;
    this.#startPositionInComponent.x =
      positionEvent.clientX - draggingComponent.getBoundingClientRect().x;
    this.#startPositionInComponent.y =
      positionEvent.clientY - draggingComponent.getBoundingClientRect().y;
    this.#initialScrollPosition = dropZone?.scrollTop || 0;
  };

  #onDrag: DragCallback = (
    draggableComponent: DraggableComponent,
    event: PointerEvent,
  ) => {
    this.#dropzones.forEach((dropzone) => {
      dropzone.dropzoneService.onDrag(draggableComponent, event);
    });

    const positionEvent =
      window.TouchEvent && event instanceof TouchEvent
        ? event.touches[0]
        : event;

    const relativePosition = {
      x: positionEvent.clientX - this.#startPositionDrag.x,
      y: positionEvent.clientY - this.#startPositionDrag.y,
    };

    if (this.#dropper) {
      gsap.to(this.#dropper, 0, {
        left: this.#startPositionDropper.x + relativePosition.x,
        top: this.#startPositionDropper.y + relativePosition.y,
      });
    }

    const dropZone = this.#getDropZoneByDraggable(draggableComponent);

    this.#handleCollidingDropZones(draggableComponent, dropZone);
  };

  #handleCollidingDropZones(
    draggableComponent: DraggableComponent,
    dropZone: DropZoneComponent | null,
  ) {
    const currentDraggable = (this.#dropper ??
      draggableComponent) as Draggable.TestObject;

    const collidingDropzone = this.#dropzones.find((dropZone) => {
      return Draggable.hitTest(currentDraggable, dropZone);
    });

    this.#dropzones.forEach((zone) => {
      if (zone !== collidingDropzone) {
        zone.setColliding(false);
        zone.dropzoneService.externalDropCancel();
      }
    });

    const collidingContainer = this.#draggableContainers.find((container) =>
      Draggable.hitTest(currentDraggable, container as Draggable.TestObject),
    );

    this.#draggableContainers.forEach((container) => {
      if (container !== collidingContainer) {
        container.setColliding(false);
      }
    });
    collidingContainer?.setColliding(true);

    if (collidingDropzone === dropZone || collidingDropzone?.disabled) {
      return;
    }

    if (collidingDropzone !== this.#currentCollision) {
      if (collidingDropzone) {
        collidingDropzone.setColliding(true);
        collidingDropzone.dropzoneService.externalDropStart();
      } else {
        this.#currentCollision?.dropzoneService.externalDropCancel();
      }
    }

    this.#currentCollision = collidingDropzone;
  }

  resetCollidingDropZones() {
    this.#draggableContainers.forEach((collidingContainer) => {
      collidingContainer.setColliding(false);
    });

    this.#dropzones.forEach((dropzone) => {
      dropzone.setColliding(false);
    });
  }

  #currentCollision?: DropZoneComponent;

  #onRelease: DragCallback = (releasingComponent: DraggableComponent) => {
    this.resetCollidingDropZones();

    if (this.#dropper) {
      releasingComponent.show();
    }
    releasingComponent.disablePress();

    const dropzoneByDraggable =
      this.#getDropZoneByDraggable(releasingComponent);

    if (dropzoneByDraggable && this.#dropper) {
      this.#handleReleasingComponent(dropzoneByDraggable, releasingComponent);
    }

    if (this.#standaloneDraggables.includes(releasingComponent)) {
      this.#onReleaseStandAloneDraggable(releasingComponent);
    }

    if (
      !!dropzoneByDraggable &&
      dropzoneByDraggable !== this.#currentCollision &&
      !!this.#currentCollision
    ) {
      if (!this.#currentCollision) {
        return;
      }
      if (this.#currentCollision.draggableComponents.length === 0) {
        this.#currentCollision.dropzoneService.onRelease(
          releasingComponent,
          true,
        );
        dropzoneByDraggable.dispatchEvent(
          new DraggableComponentEvent('draggable-left', releasingComponent),
        );
      } else if (this.#currentCollision.singleValue) {
        dropzoneByDraggable.dropzoneService.setValue(
          this.#currentCollision.draggableComponents[0],
        );
        this.#currentCollision.dropzoneService.setValue(releasingComponent);
      }
    } else if (releasingComponent) {
      this.#handleDuplicateDraggables(releasingComponent, dropzoneByDraggable);
      this.#dropzones.forEach((dropzone) => {
        dropzone.dropzoneService.onRelease(
          releasingComponent,
          !!dropzoneByDraggable || dropzone === this.#currentCollision,
        );
      });
    }

    // eslint-disable-next-line no-param-reassign
    releasingComponent.clicked = false;

    setTimeout(
      () => {
        releasingComponent.enablePress();
        this.#currentCollision = undefined;
      },
      // hacky but otherwise animations will overlap and do some magic stuff to the positioning.
      DragDropService.ANIMATION_TIME_IN_SECONDS * 1000 +
        DragDropService.DELAY_AFTER_ANIMATION,
    );
  };

  #handleDuplicateDraggables(
    releasingComponent: DraggableComponent,
    dropzoneByDraggable: DropZoneComponent | null,
  ) {
    if (
      (releasingComponent.hasDuplicates || releasingComponent.wasDuplicate) &&
      !this.#currentCollision
    ) {
      releasingComponent.remove();
      dropzoneByDraggable?.dispatchEvent(
        new DraggableComponentEvent('draggable-left', releasingComponent),
      );
    }

    if (releasingComponent.hasDuplicates) {
      releasingComponent.setWasDuplicate();
    }
  }

  #handleReleasingComponent(
    dropZone: DropZoneComponent,
    releasingComponent: DraggableComponent,
  ) {
    if (dropZone.dropzoneService.noChangesMade(releasingComponent)) {
      this.#onReleaseNoChangesMade(dropZone, releasingComponent);
    } else {
      this.#onReleaseChangesMade(dropZone, releasingComponent);
    }

    setTimeout(
      () => {
        this.#dropper?.remove();
        this.#dropper = undefined;
      },
      // hacky but otherwise animations will overlap and do some magic stuff to the positioning.
      DragDropService.ANIMATION_TIME_IN_SECONDS * 1000 +
        DragDropService.DELAY_AFTER_ANIMATION,
    );
  }

  #onReleaseStandAloneDraggable(releasingComponent: DraggableComponent) {
    if (this.#currentCollision) {
      this.#standaloneDraggables = this.#standaloneDraggables.filter(
        (standAlone) => standAlone !== releasingComponent,
      );
      this.#currentCollision.dropzoneService.animateDraggableToCorrectPosition(
        releasingComponent,
        {
          x: -this.#startPositionDrag.x + this.#startPositionInComponent.x,
          y: -this.#startPositionDrag.y + this.#startPositionInComponent.y,
        },
      );

      if (this.#dropper) {
        this.#moveStandaloneDropperToNewPosition(
          this.#dropper,
          this.#currentCollision,
          releasingComponent,
        );
      }
    } else {
      this.#dropper?.remove();
      releasingComponent.resetPosition();
    }
  }

  #moveStandaloneDropperToNewPosition(
    dropper: HTMLElement,
    dropZone: DropZoneComponent,
    releasingComponent: DraggableComponent,
  ) {
    const rect = dropZone.getBoundingClientRect();
    gsap.to(dropper, DragDropService.ANIMATION_TIME_IN_SECONDS, {
      x: 0,
      y: 0,
      left: rect.x,
      top: dropZone.dropzoneService.getRelativePositionIndex(
        releasingComponent.index,
      ),
      scale: 1,
    });

    this.#removeDropperWhenAnimationIsDone();
  }

  #onReleaseNoChangesMade(
    dropZone: DropZoneComponent,
    releasingComponent: DraggableComponent,
  ) {
    if (this.#dropper) {
      const isStandalone =
        this.#standaloneDraggables.includes(releasingComponent);
      if (this.#resetDropperOutside || isStandalone) {
        const newYPosition =
          this.#startPositionDropper.y +
          this.#initialScrollPosition -
          dropZone.scrollTop;
        gsap.to(this.#dropper, DragDropService.ANIMATION_TIME_IN_SECONDS, {
          left: this.#startPositionDropper.x,
          top: newYPosition,
          scale: 1,
        });

        this.#removeDropperWhenAnimationIsDone();
      } else {
        this.#dropper?.remove();
      }
    }
  }

  #removeDropperWhenAnimationIsDone() {
    setTimeout(
      () => {
        this.#dropper?.remove();
      },
      // hacky but otherwise animations will overlap and do some magic stuff to the positioning.
      DragDropService.ANIMATION_TIME_IN_SECONDS * 1000 +
        DragDropService.DELAY_BROADCAST_VALUE,
    );
  }

  #onReleaseChangesMade(
    dropZone: DropZoneComponent,
    releasingComponent: DraggableComponent,
  ) {
    if (this.#dropper) {
      gsap.to(this.#dropper, DragDropService.ANIMATION_TIME_IN_SECONDS, {
        left:
          this.#startPositionDropper.x +
          dropZone.dropzoneService.getPaddingLeft(),
        top: dropZone.dropzoneService.getRelativePositionIndex(
          releasingComponent.index,
        ),
        y: 0,
        scale: 1,
      });
    }
  }

  #initializeDropper(
    draggingComponent: DraggableComponent,
    event: PointerEvent,
  ) {
    this.#dropper = draggingComponent.cloneNode(true) as HTMLElement;
    if (this.#parentNode) {
      this.#parentNode.prepend(this.#dropper);
    } else {
      document.body.prepend(this.#dropper);
    }

    gsap.to(this.#dropper, 0, this.#getCloneStyle(draggingComponent, event));

    gsap.to(this.#dropper, DragDropService.ANIMATION_TIME_IN_SECONDS, {
      scale: DragDropService.ANIMATION_SCALE,
      zIndex: '+=1000',
    });

    draggingComponent.hide();
  }

  #getCloneStyle(draggable: DraggableComponent, event: PointerEvent) {
    const draggableRect = draggable.getBoundingClientRect();
    const dropZone = this.#getDropZoneByDraggable(draggable);

    const positionEvent =
      window.TouchEvent && event instanceof TouchEvent
        ? event.touches[0]
        : event;

    if (dropZone) {
      this.#startPositionDropper =
        dropZone?.dropzoneService.getStartPositionDraggable(draggable);
    } else {
      const rect = draggable.getBoundingClientRect();

      const relativePositionInDraggingObject = {
        x: positionEvent.clientX - rect.x,
        y: positionEvent.clientY - rect.y,
      };

      const middleOfDraggable = {
        x: rect.width / 2,
        y: rect.height / 2,
      };

      this.#startPositionDropper = {
        x: rect.x - (middleOfDraggable.x - relativePositionInDraggingObject.x),
        y: rect.y - (middleOfDraggable.y - relativePositionInDraggingObject.y),
      };
    }

    return {
      x: 0,
      y: 0,
      left: this.#startPositionDropper.x,
      top: this.#startPositionDropper.y,
      margin: 0,
      position: 'fixed',
      width: draggableRect.width,
      height: draggableRect.height,
    };
  }

  #getDropZoneByDraggable(
    draggable: DraggableComponent,
  ): DropZoneComponent | null {
    return this.#dropzones.reduce<DropZoneComponent | null>(
      (result, dropZone) => {
        if (result) {
          return result;
        }

        return dropZone.dropzoneService.containsDraggable(draggable)
          ? dropZone
          : result;
      },
      null,
    );
  }

  constructor(
    parentNode: ParentNode | undefined = undefined,
    disableGsapAutoscroll = false,
  ) {
    this.#disableGsapAutoscroll = disableGsapAutoscroll;
    this.#parentNode = parentNode;
    gsap.registerPlugin(Draggable);
  }
}
